import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Breadcrumbs from "examples/Breadcrumbs";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import ReactPlayer from "react-player";
import { call } from "services/axiosService/core";
import { useMaterialUIController } from "context";
import palette from "assets/theme/base/palette";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

const TYPE_ICON = {
  video: "movie",
  article: "article",
  infographic: "leaderboard",
  worksheet: "note",
};

const PlayContent = () => {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { profile } = controller;
  const { contentTitle } = useParams();
  const [content, setContent] = useState(null);
  const [userMetricsData, setUserMetricsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metricsUpdated, setMetricsUpdated] = useState(false);

  useEffect(() => {
    if (profile.id) {
      Promise.all([fetchContent(), fetchMetrics()]).finally(() => setLoading(false));
    }
  }, [profile.id]);

  const fetchContent = () =>
    call(`content/${contentTitle}`, "GET", {})
      .then((res) => {
        if (res.status === 200) setContent(res.data);
      })
      .catch((err) => Sentry.captureException(err));

  const fetchMetrics = () =>
    call(`users/${profile.id}/contentmetrics`, "GET", {})
      .then((res) => {
        if (res.status === 200) setUserMetricsData(res.data);
      })
      .catch((err) => Sentry.captureException(err));

  const markViewed = () => {
    if (metricsUpdated || !userMetricsData || !content) return;
    const { title } = content;
    let viewed = userMetricsData.viewedcontent || [];
    let weekly = userMetricsData.weeklycontent || [];
    if (!viewed.includes(title)) viewed = [...viewed, title];
    if (!weekly.includes(title)) weekly = [...weekly, title];
    const updated = { ...userMetricsData, viewedcontent: viewed, weeklycontent: weekly, eventkey: { event: "viewcontent", value: title } };
    call(`users/${profile.id}/contentmetrics`, "PUT", { body: updated })
      .then((res) => {
        if (res.status === 200) {
          setUserMetricsData(updated);
          setMetricsUpdated(true);
        }
      })
      .catch((err) => Sentry.captureException(err));
  };

  const openExternal = () => {
    markViewed();
    window.open(content.content_url, "_blank", "noreferrer");
  };

  const { content_url, content_type, open_player, title, thumbnail_url, description } =
    content || {};
  const typeIcon = TYPE_ICON[content_type?.toLowerCase()] || "description";
  const isVideo = open_player;

  return (
    <DashboardLayout>
      <DashboardNavbar
        breadcrumbs={
          <Breadcrumbs
            icon="home"
            title={title || contentTitle}
            route={[t("pageTitles.contentLibrary") || "Content Library", title || contentTitle]}
            light={false}
          />
        }
      />

      <MDBox mt={3} mb={4}>
        {loading ? (
          <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Skeleton variant="rectangular" height={420} />
            <MDBox p={3}>
              <Skeleton width="60%" height={32} />
              <Skeleton width="40%" height={24} sx={{ mt: 1 }} />
            </MDBox>
          </Card>
        ) : content ? (
          <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
            {isVideo ? (
              /* ── Video Player ── */
              <>
                <MDBox
                  sx={{
                    position: "relative",
                    width: "100%",
                    bgcolor: "#000",
                    lineHeight: 0,
                  }}
                  onPlay={markViewed}
                >
                  <ReactPlayer
                    url={content_url}
                    controls
                    width="100%"
                    height="480px"
                    onPlay={markViewed}
                    config={{
                      youtube: {
                        playerVars: { rel: 0, modestbranding: 1, iv_load_policy: 3 },
                      },
                    }}
                  />
                </MDBox>

                <MDBox p={{ xs: 2.5, md: 3.5 }}>
                  <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                    <Chip
                      icon={<Icon sx={{ fontSize: "14px !important" }}>{typeIcon}</Icon>}
                      label={content_type}
                      size="small"
                      sx={{ textTransform: "capitalize", fontWeight: "bold" }}
                    />
                  </MDBox>
                  <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
                    {title}
                  </MDTypography>
                  {description && (
                    <MDTypography variant="body2" color="secondary" lineHeight={1.7} mt={1}>
                      {description}
                    </MDTypography>
                  )}
                </MDBox>
              </>
            ) : (
              /* ── External Content (article / infographic / worksheet) ── */
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{ py: { xs: 6, md: 10 }, px: { xs: 3, md: 6 }, textAlign: "center" }}
              >
                {thumbnail_url ? (
                  <MDBox
                    component="img"
                    src={thumbnail_url}
                    alt={title}
                    sx={{
                      width: "100%",
                      maxWidth: 480,
                      maxHeight: 280,
                      objectFit: "cover",
                      borderRadius: 3,
                      mb: 4,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    }}
                  />
                ) : (
                  <MDBox
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      bgcolor: palette.brand[50],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    <Icon sx={{ fontSize: "48px !important", color: palette.brand[500] }}>
                      {typeIcon}
                    </Icon>
                  </MDBox>
                )}

                <Chip
                  icon={<Icon sx={{ fontSize: "14px !important" }}>{typeIcon}</Icon>}
                  label={content_type}
                  size="small"
                  sx={{ mb: 2, textTransform: "capitalize", fontWeight: "bold" }}
                />

                <MDTypography variant="h4" fontWeight="bold" mb={1.5}>
                  {title}
                </MDTypography>

                {description && (
                  <MDTypography
                    variant="body1"
                    color="secondary"
                    lineHeight={1.75}
                    mb={4}
                    sx={{ maxWidth: 560 }}
                  >
                    {description}
                  </MDTypography>
                )}

                <MDButton
                  variant="contained"
                  color="info"
                  size="large"
                  onClick={openExternal}
                  sx={{ borderRadius: 2, px: 5, py: 1.5 }}
                  startIcon={<Icon>open_in_new</Icon>}
                >
                  {t("playContent.openInNewTab") || "Open Content"}
                </MDButton>

                <MDTypography variant="caption" color="secondary" mt={2}>
                  {t("playContent.openInNewTab") || "Opens in a new tab"}
                </MDTypography>
              </MDBox>
            )}
          </Card>
        ) : (
          <MDBox textAlign="center" py={10}>
            <Icon sx={{ fontSize: 56, color: "text.secondary", opacity: 0.3 }}>
              error_outline
            </Icon>
            <MDTypography variant="h6" color="secondary" mt={2}>
              {t("playContent.notFound") || "Content not found"}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
};

export default PlayContent;
